import isNil from 'lodash/isNil';
import { CONTROL_TYPES } from '@/components/AppInput.vue';

// User-selectable filter operators. Wire convention (shared with paydays-api): ?field[op]=value; bare ?field=value === eq.
export const OPERATORS = {
  EQ: 'eq',
  NE: 'ne',
  IN: 'in',
  NIN: 'nin',
  GT: 'gt',
  GTE: 'gte',
  LT: 'lt',
  LTE: 'lte',
  CONTAINS: 'contains',
  BETWEEN: 'between',
};

export const OPERATOR_LABELS = {
  [OPERATORS.EQ]: 'Equals',
  [OPERATORS.NE]: 'Not equal',
  [OPERATORS.IN]: 'Any of',
  [OPERATORS.NIN]: 'None of',
  [OPERATORS.GT]: 'Greater than',
  [OPERATORS.GTE]: 'Greater than or equal',
  [OPERATORS.LT]: 'Less than',
  [OPERATORS.LTE]: 'Less than or equal',
  [OPERATORS.CONTAINS]: 'Contains',
  [OPERATORS.BETWEEN]: 'Between',
};

// Compact symbols for the applied-filter chips, so users can see exactly which operator is active.
export const OPERATOR_SYMBOLS = {
  [OPERATORS.EQ]: '=',
  [OPERATORS.NE]: '≠',
  [OPERATORS.IN]: '=',
  [OPERATORS.NIN]: '≠',
  [OPERATORS.GT]: '>',
  [OPERATORS.GTE]: '≥',
  [OPERATORS.LT]: '<',
  [OPERATORS.LTE]: '≤',
  [OPERATORS.CONTAINS]: 'contains',
  [OPERATORS.BETWEEN]: 'between',
};

// Semantic filter types drive the default operator menu (distinct from AppInput's widget CONTROL_TYPES).
export const FILTER_TYPES = {
  CATEGORICAL: 'categorical',
  DATE: 'date',
  NUMBER: 'number',
  TEXT: 'text',
};

const OPERATORS_BY_TYPE = {
  [FILTER_TYPES.CATEGORICAL]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.IN],
  [FILTER_TYPES.DATE]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.GT, OPERATORS.GTE, OPERATORS.LT, OPERATORS.LTE],
  [FILTER_TYPES.NUMBER]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.GT, OPERATORS.GTE, OPERATORS.LT, OPERATORS.LTE],
  [FILTER_TYPES.TEXT]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.CONTAINS],
};

// AppInput widget type -> semantic filter type, so existing column descriptors get sane defaults.
const CONTROL_TYPE_TO_FILTER_TYPE = {
  [CONTROL_TYPES.CHECKBOX]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.CHECKBOX_SINGLE]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.RADIO]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.SELECT]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.DATE]: FILTER_TYPES.DATE,
  [CONTROL_TYPES.TIME]: FILTER_TYPES.NUMBER,
  [CONTROL_TYPES.NUMBER]: FILTER_TYPES.NUMBER,
  [CONTROL_TYPES.TEXT]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.INPUT]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.EMAIL]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.TEL]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.TEXTAREA]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.PASSWORD]: FILTER_TYPES.TEXT,
};

export const filterTypeForControl = controlType => CONTROL_TYPE_TO_FILTER_TYPE[controlType] || FILTER_TYPES.TEXT;

export const operatorsForType = filterType => OPERATORS_BY_TYPE[filterType] || [OPERATORS.EQ];

// Operators a column offers: explicit `operators` wins, else the semantic-type default, else eq-only.
export const availableOperators = filter =>
  filter?.operators?.length ? filter.operators : operatorsForType(filterTypeForControl(filter?.type));

// Default/fixed operator for a column (first offered, or explicit defaultOperator, else eq).
export const operatorFor = filter => filter?.defaultOperator || availableOperators(filter)[0] || OPERATORS.EQ;

const IN_OPERATORS = [OPERATORS.IN, OPERATORS.NIN];
export const isMultiValueOperator = op => IN_OPERATORS.includes(op);

// Ready-made operator lists for column descriptors, so callers don't re-spell them per column.
export const COMPARISON_OPERATORS = [
  OPERATORS.GTE,
  OPERATORS.LTE,
  OPERATORS.BETWEEN,
  OPERATORS.EQ,
  OPERATORS.NE,
  OPERATORS.GT,
  OPERATORS.LT,
];
export const TEXT_MATCH_OPERATORS = [OPERATORS.CONTAINS, OPERATORS.EQ, OPERATORS.NE];

// Accept both the flat scalar model ({key: value}) and the structured one ({key: {op, value}}).
const normalizeEntry = entry =>
  entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry
    ? { op: entry.op || OPERATORS.EQ, value: entry.value }
    : { op: OPERATORS.EQ, value: entry };

// Shared "don't send this" test. Also treats a range object with no bounds as empty.
export const isEmptyValue = value => {
  if (isNil(value) || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value);
  }
  if (typeof value === 'string') {
    // Anchored: a text value merely containing "false" (e.g. false@acme.com) is a real filter.
    return value.trim().toLowerCase() === 'false';
  }
  if (typeof value === 'object') {
    return isEmptyValue(value.from) && isEmptyValue(value.to);
  }
  return false;
};

const OP_KEY_RE = /^(.+)\[(\w+)\]$/;

// {key: {op, value}} (or flat {key: value}) -> flat query/param object; eq emits bare `key`, non-eq emits `key[op]`.
export const serializeFilters = (filters = {}) =>
  Object.entries(filters).reduce((acc, [key, entry]) => {
    const { op, value } = normalizeEntry(entry);
    // `between` has no native backend operator — split it into gte + lte, which the translator already understands.
    if (op === OPERATORS.BETWEEN) {
      const { from, to } = value || {};
      if (!isEmptyValue(from)) {
        acc[`${key}[${OPERATORS.GTE}]`] = from;
      }
      if (!isEmptyValue(to)) {
        acc[`${key}[${OPERATORS.LTE}]`] = to;
      }
      return acc;
    }
    if (isEmptyValue(value)) {
      return acc;
    }
    acc[op === OPERATORS.EQ ? key : `${key}[${op}]`] = value;
    return acc;
  }, {});

// Scale numeric filter values by a per-field factor (e.g. money major units -> integer minor units the backend stores).
export const scaleFilterValues = (structured = {}, factorByField = {}) =>
  Object.entries(structured).reduce((acc, [key, entry]) => {
    const factor = factorByField[key];
    if (!factor) {
      acc[key] = entry;
      return acc;
    }
    const { op, value } = normalizeEntry(entry);
    const scale = v => (isEmptyValue(v) ? v : Math.round(Number(v) * factor));
    if (op === OPERATORS.BETWEEN) {
      const { from, to } = value || {};
      acc[key] = { op, value: { from: scale(from), to: scale(to) } };
    } else if (Array.isArray(value)) {
      acc[key] = { op, value: value.map(scale) };
    } else {
      acc[key] = { op, value: scale(value) };
    }
    return acc;
  }, {});

// flat query object -> {key: {op, value}}; bare `key` hydrates as eq. A key carrying both gte+lte recombines to `between`.
export const parseFiltersFromQuery = (query = {}) => {
  const byKey = {};
  Object.entries(query).forEach(([rawKey, value]) => {
    const match = rawKey.match(OP_KEY_RE);
    const key = match ? match[1] : rawKey;
    const op = match ? match[2] : OPERATORS.EQ;
    byKey[key] = byKey[key] || [];
    byKey[key].push({ op, value });
  });
  const validOps = Object.values(OPERATORS);
  return Object.entries(byKey).reduce((acc, [key, all]) => {
    // Drop unknown operators from a hand-edited/stale URL — sending them back would 400.
    const conds = all.filter(c => validOps.includes(c.op));
    if (!conds.length) {
      return acc;
    }
    const gte = conds.find(c => c.op === OPERATORS.GTE);
    const lte = conds.find(c => c.op === OPERATORS.LTE);
    if (conds.length === 2 && gte && lte) {
      acc[key] = { op: OPERATORS.BETWEEN, value: { from: gte.value, to: lte.value } };
    } else {
      const { op, value } = conds[0];
      // A single ?source[in]=A parses as a string; multi-value operators must stay arrays.
      acc[key] = { op, value: isMultiValueOperator(op) && !Array.isArray(value) ? [value] : value };
    }
    return acc;
  }, {});
};

// flat {key: value} -> structured {key: {op, value}} (per-key op via `defaults`, else eq).
export const toStructured = (flat = {}, defaults = {}) =>
  Object.entries(flat).reduce((acc, [key, value]) => {
    acc[key] = { op: defaults[key] || OPERATORS.EQ, value };
    return acc;
  }, {});

// Reject a typed value the backend would 400 on (numbers must be finite, dates must be complete).
export const isValidTypedValue = (filterType, value) => {
  if (filterType === FILTER_TYPES.NUMBER) {
    return Number.isFinite(Number(value));
  }
  if (filterType === FILTER_TYPES.DATE) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value));
  }
  return true;
};
